import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { INCIDENT_REPORT_STATUS } from "@/types/incident.types";

export const INCIDENT_STATUS_BADGE_CLASS: Record<INCIDENT_REPORT_STATUS, string> = {
    [INCIDENT_REPORT_STATUS.REPORTED]: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
    [INCIDENT_REPORT_STATUS.ASSIGNED]: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
    [INCIDENT_REPORT_STATUS.RESOLVED]: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    [INCIDENT_REPORT_STATUS.INVALID]: "bg-red-500/15 text-red-700 dark:text-red-400",
};

export const getIncidentStatusTranslationKey = (status: INCIDENT_REPORT_STATUS) =>
    status === INCIDENT_REPORT_STATUS.ASSIGNED
        ? "incident_status_reviewing"
        : `incident_status_${status.toLowerCase()}`;

const BADGE_SIZE_CLASS = {
    default: "rounded-md px-2 py-0.5 text-xs font-medium",
    compact: "rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
    detail: "rounded-md px-2.5 py-1 text-xs font-semibold",
} as const;

type IncidentStatusBadgeProps = {
    status: INCIDENT_REPORT_STATUS;
    size?: keyof typeof BADGE_SIZE_CLASS;
    className?: string;
};

function IncidentStatusPing({ compact }: { compact?: boolean }) {
    const dotSize = compact ? "size-1.5" : "size-2";

    return (
        <span className={cn("relative flex shrink-0", dotSize)}>
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-blue-500 opacity-75" />
            <span className={cn("relative inline-flex rounded-full bg-blue-600", dotSize)} />
        </span>
    );
}

export function IncidentStatusBadge({ status, size = "default", className }: IncidentStatusBadgeProps) {
    const { t } = useTranslation();
    const isReviewing = status === INCIDENT_REPORT_STATUS.ASSIGNED;

    return (
        <span
            className={cn(
                "inline-flex items-center",
                size === "compact" ? "gap-1" : "gap-1.5",
                BADGE_SIZE_CLASS[size],
                INCIDENT_STATUS_BADGE_CLASS[status],
                className
            )}
        >
            {isReviewing && <IncidentStatusPing compact={size === "compact"} />}
            {t(getIncidentStatusTranslationKey(status))}
        </span>
    );
}
